// adminportal/src/config.ts
export type RuntimeConfig = {
  apiUrl: string;
  workflowsApiUrl?: string;
  transferMonitoringApiUrl?: string;
  companyName?: string;
  cognito?: {
    userPoolId: string;
    userPoolClientId: string;
    region: string;
  };
};

let _config: RuntimeConfig = {
  // fallback to build-time env if present, else blank
  apiUrl: (import.meta as any)?.env?.VITE_API_BASE ?? '',
};

function normalizeBase(url: string) {
  if (!url) return '';
  try {
    // Ensure exactly one trailing slash
    return `${url.replace(/\/+$/, '')}/`;
  } catch {
    return url.endsWith('/') ? url : `${url}/`;
  }
}

export async function loadConfig(): Promise<RuntimeConfig> {
  try {
    const res = await fetch('/config.json', { cache: 'no-store' });
    if (!res.ok) {
      // Keep fallback if file not found (e.g., local dev)
      return { ..._config, apiUrl: normalizeBase(_config.apiUrl) };
    }
    const json = (await res.json()) as Partial<RuntimeConfig>;
    const apiUrl = normalizeBase(String(json.apiUrl ?? ''));
    const workflowsApiUrl = json.workflowsApiUrl
      ? normalizeBase(String(json.workflowsApiUrl))
      : undefined;
    const transferMonitoringApiUrl = json.transferMonitoringApiUrl
      ? normalizeBase(String(json.transferMonitoringApiUrl))
      : undefined;
    const companyName = json.companyName;
    const cognito = json.cognito;
    return { apiUrl, workflowsApiUrl, transferMonitoringApiUrl, companyName, cognito };
  } catch {
    // Network failure -> keep fallback
    return { ..._config, apiUrl: normalizeBase(_config.apiUrl) };
  }
}

export function setGlobalConfig(cfg: RuntimeConfig) {
  _config = {
    apiUrl: normalizeBase(cfg.apiUrl),
    workflowsApiUrl: cfg.workflowsApiUrl ? normalizeBase(cfg.workflowsApiUrl) : undefined,
    transferMonitoringApiUrl: cfg.transferMonitoringApiUrl
      ? normalizeBase(cfg.transferMonitoringApiUrl)
      : undefined,
    companyName: cfg.companyName,
    cognito: cfg.cognito,
  };
  // Also set on window for components that need it
  (window as any).__APP_CONFIG = {
    apiUrl: normalizeBase(cfg.apiUrl),
    workflowsApiUrl: cfg.workflowsApiUrl ? normalizeBase(cfg.workflowsApiUrl) : undefined,
    transferMonitoringApiUrl: cfg.transferMonitoringApiUrl
      ? normalizeBase(cfg.transferMonitoringApiUrl)
      : undefined,
    companyName: cfg.companyName,
    cognito: cfg.cognito,
  };
  // Set Cognito config separately for easy access
  if (cfg.cognito) {
    (window as any).__COGNITO_CONFIG = cfg.cognito;
  }
}

export function getConfig(): RuntimeConfig {
  return _config;
}

// Helper to check if API URL is configured
export function isApiUrlConfigured(): boolean {
  const base = getConfig().apiUrl || '';
  return !!base && !base.includes('placeholder.invalid');
}

// Helper to join paths safely
export function apiUrl(path: string) {
  const base = getConfig().apiUrl || '';
  if (!base || base.includes('placeholder.invalid')) {
    // Return empty string to signal API is not configured
    // Components should check isApiUrlConfigured() before calling apiUrl()
    return '';
  }
  const left = base.replace(/\/+$/, '');
  const right = path.replace(/^\/+/, '');
  return `${left}/${right}`;
}

// Helper for workflows API
export function workflowsApiUrl(path: string) {
  const config = getConfig();

  // For endpoints that are only available in the main admin API, use apiUrl
  const mainApiOnlyEndpoints = ['lambda-functions', 's3-buckets', 'functions', 'buckets'];
  const useMainApi = mainApiOnlyEndpoints.some(endpoint => path.includes(endpoint));

  const base = useMainApi ? config.apiUrl : config.workflowsApiUrl || config.apiUrl;
  if (!base || base.includes('placeholder.invalid')) {
    return 'https://placeholder.invalid/api/' + path.replace(/^\/+/, '');
  }
  const left = base.replace(/\/+$/, '');
  const right = path.replace(/^\/+/, '');

  // For main admin API, add /workflows/ prefix for workflow endpoints
  if (useMainApi) {
    return `${left}/workflows/${right}`;
  }

  // For workflows API, don't add prefix (routes are /servers, /roles, etc.)
  return `${left}/${right}`;
}

// Helper for transfer monitoring API
export function transferMonitoringApiUrl(path: string) {
  const base = getConfig().transferMonitoringApiUrl || getConfig().apiUrl;
  if (!base || base.includes('placeholder.invalid')) {
    return 'https://placeholder.invalid/api/' + path.replace(/^\/+/, '');
  }
  const left = base.replace(/\/+$/, '');
  const right = path.replace(/^\/+/, '');
  return `${left}/${right}`;
}

// Helper to get company name from config
export function getCompanyName(): string {
  return getConfig().companyName || 'Northeast Georgia Health System';
}
